// src/redux/sagas/widgetDataEnrichmentSaga.ts
import { put, select, takeEvery } from 'redux-saga/effects';
import { SiteContent } from '../../types/siteContent';
import { RootState } from '../slices/rootReducer';
import { fetchMenuItemsSuccess } from '../slices/menuItemsSlice';
import { fetchSiteContentSuccess } from '../slices/siteContentSlice';
import { updateWidgetsWithMenuData } from '../../utils/widgetDataUpdater';

/**
 * Enriches widgets with menu data after menu items are successfully loaded
 * This populates popular item widgets with actual menu data
 */
function* populateWidgetsWithMenuData(action: any): Generator<any, void, any> {
  try {
    const menuItems = action.payload;
    const siteContentData: SiteContent | null = yield select((state: RootState) => state.siteContent.data);
    const websiteMetadata: any = yield select((state: RootState) => state.siteContent.websiteConfig);
    
    // Early return if no site content available
    if (!siteContentData) return;
    
    // Check if we have popular widgets that need menu data
    const hasPopularWidgets = siteContentData.homeWidgets?.some(
      (widget: any) =>
        widget.type.toLowerCase().includes('popular') ||
        widget.type.toLowerCase().includes('popularitems')
    );
    
    // Skip if no popular widgets found
    if (!hasPopularWidgets) return;
    
    // Enrich widgets with menu data
    let enrichedWidgets = siteContentData.widgets;
    let enrichedHomeWidgets = siteContentData.homeWidgets;
    
    if (enrichedWidgets) {
      enrichedWidgets = updateWidgetsWithMenuData(enrichedWidgets as any, menuItems) as any;
    }
    if (enrichedHomeWidgets) {
      enrichedHomeWidgets = updateWidgetsWithMenuData(enrichedHomeWidgets as any, menuItems) as any;
    }
    
    // Create updated site content with enriched widgets
    const enrichedSiteContent = {
      ...siteContentData,
      widgets: enrichedWidgets,
      homeWidgets: enrichedHomeWidgets,
    };
    
    // Dispatch success with enriched data
    yield put(fetchSiteContentSuccess({
      siteContent: enrichedSiteContent as unknown as SiteContent,
      websiteConfig: websiteMetadata
    }));
  } catch (error) {
    console.warn('Failed to enrich widgets with menu data:', error);
  }
}

/**
 * Watcher saga for widget data enrichment
 * Listens for menu items success and triggers widget population
 */
export function* watchWidgetDataEnrichment() {
  yield takeEvery(fetchMenuItemsSuccess.type, populateWidgetsWithMenuData);
}
